<template>
  <b-modal
    ref="modal"
    :id="modalId"
    centered
    hide-footer
    title="Terms and Conditions - Service Bond Agreement (Ikatan Dinas)"
    size="xl"
    scrollable
    @hidden="onHidden"
  >
    <div class="terms-modal-content">
      <!-- Service Bond Warning Notice -->
      <div class="alert alert-warning mb-4" role="alert">
        <div class="d-flex align-items-center">
          <i class="fe fe-alert-triangle me-3" style="font-size: 1.5rem"></i>
          <div>
            <h6 class="alert-heading mb-1">
              Service Bond (Ikatan Dinas) Required
            </h6>
            <p class="mb-0 small">
              This training requires a service bond (Ikatan Dinas) commitment.
              Please read the terms and conditions carefully before proceeding.
            </p>
          </div>
        </div>
      </div>

      <!-- PDF Viewer Container -->
      <div class="pdf-container mb-4">
        <div
          class="pdf-header d-flex justify-content-between align-items-center mb-2"
        >
          <h6 class="mb-0 font-weight-bold">Terms and Conditions Document</h6>
          <div class="d-flex align-items-center gap-2">
            <span
              class="badge"
              :class="hasScrolledToEnd ? 'bg-success' : 'bg-secondary'"
            >
              <i
                class="fe"
                :class="hasScrolledToEnd ? 'fe-check-circle' : 'fe-file-text'"
              ></i>
              {{ hasScrolledToEnd ? "Document Read" : "Please Read Document" }}
            </span>
          </div>
        </div>

        <!-- PDF Iframe -->
        <div class="pdf-viewer-wrapper">
          <div
            class="pdf-scroll-container"
            ref="pdfScrollContainer"
            @scroll="onPdfScroll"
          >
            <iframe
              ref="pdfIframe"
              :src="`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=100`"
              class="pdf-viewer"
              @load="onPdfLoad"
            >
              <p>
                Your browser does not support PDF viewing.
                <a :href="pdfUrl" target="_blank"
                  >Click here to download the PDF</a
                >.
              </p>
            </iframe>
          </div>
        </div>

        <!-- Scroll Progress Indicator -->
        <div class="scroll-progress mt-2">
          <div class="progress" style="height: 4px">
            <div
              class="progress-bar"
              :class="hasScrolledToEnd ? 'bg-success' : 'bg-primary'"
              role="progressbar"
              :style="`width: ${scrollProgress}%`"
              :aria-valuenow="scrollProgress"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
          <small class="text-muted d-block mt-1">
            {{
              hasScrolledToEnd
                ? "Document fully read"
                : `Reading progress: ${Math.round(scrollProgress)}%`
            }}
          </small>
        </div>
      </div>

      <!-- Terms and Conditions Checkbox -->
      <div class="terms-agreement mb-4">
        <div
          class="form-check d-flex align-items-start gap-3 p-3 border rounded"
        >
          <input
            id="termsCheckbox"
            v-model="agreedToTerms"
            type="checkbox"
            class="form-check-input mt-1"
            :disabled="!hasScrolledToEnd"
            style="transform: scale(1.2)"
          />
          <label
            for="termsCheckbox"
            class="form-check-label flex-grow-1"
            :class="{ 'text-muted': !hasScrolledToEnd }"
          >
            <strong>I agree to the Terms and Conditions</strong>
            <p class="mb-0 small mt-1">
              I have read and understood the service bond agreement, including
              all terms, conditions, and obligations. I acknowledge that failure
              to comply with the service bond requirements may result in
              financial penalties and other consequences as outlined in the
              document above.
            </p>
          </label>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end gap-3">
        <b-button variant="outline-secondary" @click="closeModal">
          Cancel
        </b-button>
        <b-button
          variant="warning"
          :disabled="!canProceed"
          @click="proceedWithEnrollment"
        >
          <i class="fe fe-check-circle me-2"></i>
          Accept Terms & Continue Enrollment
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "TermsConditionsModal",

  props: {
    modalId: {
      type: String,
      default: "terms-conditions-modal",
    },
    trainingData: {
      type: Object,
      default: null,
    },
    enrollmentPayload: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      hasScrolledToEnd: false,
      agreedToTerms: false,
      scrollProgress: 0,
      pdfLoaded: false,
      pdfUrl: "/dummy-terms-conditions.pdf",
      autoReadTimeout: null,
    };
  },

  computed: {
    canProceed() {
      return this.hasScrolledToEnd && this.agreedToTerms;
    },
  },

  watch: {
    trainingData: {
      immediate: true,
      handler(newData) {
        if (newData) {
          this.resetModalState();
        }
      },
    },
  },

  methods: {
    onPdfLoad() {
      // PDF loaded, user can now start scrolling
      this.pdfLoaded = true;
      console.log("PDF loaded successfully");
    },

    onPdfScroll(event) {
      const container = event.target;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Calculate scroll progress
      const maxScroll = scrollHeight - clientHeight;

      if (maxScroll > 0) {
        this.scrollProgress = Math.min((scrollTop / maxScroll) * 100, 100);

        // Consider "fully read" when user scrolls to 90% or more
        if (this.scrollProgress >= 90) {
          this.hasScrolledToEnd = true;
        }
      } else {
        // If content doesn't need scrolling, consider it read after a delay
        if (!this.autoReadTimeout) {
          this.autoReadTimeout = setTimeout(() => {
            this.scrollProgress = 100;
            this.hasScrolledToEnd = true;
          }, 3000); // 3 seconds delay for short content
        }
      }
    },

    resetModalState() {
      this.hasScrolledToEnd = false;
      this.agreedToTerms = false;
      this.scrollProgress = 0;
      this.pdfLoaded = false;

      if (this.autoReadTimeout) {
        clearTimeout(this.autoReadTimeout);
        this.autoReadTimeout = null;
      }

      // Reset scroll position
      if (this.$refs.pdfScrollContainer) {
        this.$refs.pdfScrollContainer.scrollTop = 0;
      }
    },

    proceedWithEnrollment() {
      if (!this.canProceed) return;

      // Emit the enrollment payload to parent
      this.$emit("proceed-enrollment", this.enrollmentPayload);
      this.closeModal();
    },

    closeModal() {
      this.$bvModal.hide(this.modalId);
    },

    onHidden() {
      this.resetModalState();
      this.$emit("modal-closed");
    },
  },

  beforeDestroy() {
    if (this.autoReadTimeout) {
      clearTimeout(this.autoReadTimeout);
    }
  },
};
</script>

<style scoped>
.terms-modal-content {
  padding: 1rem 0;
}

.pdf-container {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  background-color: #fff;
}

.pdf-viewer-wrapper {
  position: relative;
  height: 600px;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  overflow: hidden;
}

.pdf-scroll-container {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.pdf-viewer {
  width: 100%;
  height: 120%; /* Make iframe taller to enable scrolling */
  min-height: 720px;
  border: none;
  display: block;
}

.scroll-progress {
  margin-top: 0.5rem;
}

.terms-agreement {
  background-color: #f8f9fa;
}

.form-check-input:disabled + .form-check-label {
  cursor: not-allowed;
  opacity: 0.6;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #f6c343;
  color: #856404;
}

.badge.bg-warning {
  background-color: #f6c343 !important;
}

.badge.bg-success {
  background-color: #00d97e !important;
}

.badge.bg-secondary {
  background-color: #6c757d !important;
}
</style>
